import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { getMenusreadyToServe } from "../Services/MenuApi";
import { toast } from "react-toastify";
import { useUser } from "../Auth/useUser";
import ProductSelector from "./Caisse/ProductSelector";

const InventoryModal = ({
  open,
  onClose,
  onSave,
  organisationId,
  selectedInventory,
  t,
}) => {
  const [state, setState] = useState({
    menus: [],
    loading: false,
    loadAllProducts: false,

    saving: false,
    inventoryDate: new Date().toISOString().split("T")[0],
    inventoryFilter: "",
    inventoryNotes: "",
    selectedEmplacement: {
      name: "main_stock",
      label: t("Stock Principal"),
      caisse_id: null,
    },
  });
  const user = useUser();
  const infoUser = user.result;
  // after
  useEffect(() => {
    if (open && organisationId) {
      if (selectedInventory) {
        setState((prev) => ({
          ...prev,
          inventoryDate: selectedInventory.date
            ? selectedInventory.date.split("T")[0]
            : new Date().toISOString().split("T")[0],
          inventoryNotes: selectedInventory.notes || "",
          loadAllProducts: true, // editing always shows the full catalog
          selectedEmplacement: {
            name: selectedInventory.emplacement || "main_stock",
            label: selectedInventory.emplacement || t("Stock Principal"),
            caisse_id: selectedInventory.caisse_id || null,
          },
        }));
      } else {
        setState((prev) => ({
          ...prev,
          menus: [],
          inventoryDate: new Date().toISOString().split("T")[0],
          inventoryNotes: "",
          inventoryFilter: "",
          loadAllProducts: false,
          selectedEmplacement: {
            name: "main_stock",
            label: t("Stock Principal"),
            caisse_id: null,
          },
        }));
      }
    }
  }, [open, organisationId, selectedInventory]);
  const buildInventoryRow = (menu, targetEmplacement) => {
    let currentStock = 0;
    let inventoryCount = 0;
    let difference = 0;
    let notes = "";

    if (targetEmplacement === "main_stock") {
      currentStock = menu.main_stock?.quantite_stock || 0;
    } else {
      const specificStock = menu.all_stocks?.find(
        (s) => s.emplacement === targetEmplacement,
      );
      currentStock = specificStock?.quantite_stock || 0;
    }

    if (selectedInventory && selectedInventory.items) {
      const savedItem = selectedInventory.items.find(
        (i) => i.produit_id === menu._id,
      );
      if (savedItem) {
        currentStock = savedItem.current_stock;
        inventoryCount = savedItem.inventory_count;
        difference = savedItem.difference;
        notes = savedItem.notes || "";
      } else {
        inventoryCount = currentStock;
      }
    } else {
      inventoryCount = currentStock;
    }

    return { ...menu, currentStock, inventoryCount, difference, notes };
  };
  useEffect(() => {
    if (open && organisationId && state.loadAllProducts) {
      loadMenus();
    }
  }, [open, organisationId, state.loadAllProducts, selectedInventory]);

 const loadMenus = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));
      
      // Pass the parameter to bypass pagination here
      const response = await getMenusreadyToServe(organisationId, { 
        limit: 10000 // OR pagination: false (depending on your backend setup)
      }); 
      
      console.log("API Response:", response);

      if (response.data.code === 200) {
        const targetEmplacement = selectedInventory
          ? selectedInventory.emplacement
          : "main_stock";

        const menusWithInventory = response.data.data.products.map((menu) =>
          buildInventoryRow(menu, targetEmplacement),
        );

        setState((prev) => ({
          ...prev,
          menus: menusWithInventory,
          loading: false,
        }));
      } else {
        setState((prev) => ({ ...prev, loading: false }));
        toast.error("Erreur serveur: Impossible de récupérer les produits");
      }
    } catch (error) {
      console.error("Catch block triggered:", error);
      toast.error("Erreur lors du chargement des produits");
      setState((prev) => ({ ...prev, loading: false }));
    }
  };
  const handleAddProduct = (product) => {
    if (!product) return;

    setState((prev) => {
      const alreadyAdded = prev.menus.some((m) => m._id === product._id);
      if (alreadyAdded) {
        toast.info(t("Ce produit est déjà dans la liste"));
        return prev;
      }
      const row = buildInventoryRow(product, prev.selectedEmplacement.name);
      return { ...prev, menus: [...prev.menus, row] };
    });
  };

  const handleRemoveMenu = (menuId) => {
    setState((prev) => ({
      ...prev,
      menus: prev.menus.filter((m) => m._id !== menuId),
    }));
  };

  const handleToggleLoadAllProducts = (checked) => {
    if (checked) {
      setState((prev) => ({ ...prev, loadAllProducts: true }));
    } else {
      setState((prev) => ({ ...prev, loadAllProducts: false, menus: [] }));
    }
  };
  const getEmplacements = () => {
    const emplacementsMap = new Map();
    emplacementsMap.set("main_stock", {
      name: "main_stock",
      caisse_id: null,
      label: t("Stock Principal"),
    });

    state.menus.forEach((menu) => {
      menu.all_stocks?.forEach((stock) => {
        if (stock.emplacement && stock.emplacement !== "central") {
          if (!emplacementsMap.has(stock.emplacement)) {
            emplacementsMap.set(stock.emplacement, {
              name: stock.emplacement,
              caisse_id: stock.caisse_id || null,
              label: stock.emplacement,
            });
          }
        }
      });
    });
    return Array.from(emplacementsMap.values());
  };

  const handleEmplacementChange = (emplacementName) => {
    // Find the full object from our list
    const allEmps = getEmplacements();
    const selectedObj =
      allEmps.find((e) => e.name === emplacementName) || allEmps[0];

    setState((prev) => {
      const updatedMenus = prev.menus.map((menu) => {
        let newStock = 0;

        if (selectedObj.name === "main_stock") {
          newStock = menu.main_stock?.quantite_stock || 0;
        } else {
          const specificStock = menu.all_stocks?.find(
            (s) => s.emplacement === selectedObj.name,
          );
          newStock = specificStock?.quantite_stock || 0;
        }

        return {
          ...menu,
          currentStock: newStock,
          inventoryCount: newStock,
          difference: 0,
        };
      });

      return {
        ...prev,
        selectedEmplacement: selectedObj,
        menus: updatedMenus,
      };
    });
  };
  const handleUpdate = (index, field, value) => {
    setState((prev) => {
      const updatedMenus = [...prev.menus];
      if (field === "inventoryCount") {
        const parsedValue = value === "" ? "" : parseFloat(value) || 0;
        updatedMenus[index][field] = parsedValue;
        console.log("parsedValue", parsedValue);
        updatedMenus[index].difference =
          parsedValue !== ""
            ? parsedValue - updatedMenus[index].currentStock
            : 0;
      } else {
        updatedMenus[index][field] = value;
      }
      return { ...prev, menus: updatedMenus };
    });
  };

  const getFilteredMenus = () => {
    const { menus, inventoryFilter } = state;
    if (!inventoryFilter) return menus;

    return menus.filter((menu) => {
      const title = menu.titre?.fr || menu.titre || "";
      const ref = menu.reference || "";
      return (
        title.toLowerCase().includes(inventoryFilter.toLowerCase()) ||
        ref.toLowerCase().includes(inventoryFilter.toLowerCase())
      );
    });
  };

  const getSummary = () => {
    const { menus } = state;
    const itemsWithChanges = menus.filter((item) => item.difference !== 0);
    const totalDifference = menus.reduce(
      (sum, item) => sum + item.difference,
      0,
    );

    return {
      totalItems: menus.length,
      itemsWithChanges: itemsWithChanges.length,
      totalDifference,
      positiveDifferences: menus.filter((item) => item.difference > 0).length,
      negativeDifferences: menus.filter((item) => item.difference < 0).length,
    };
  };

  const handleSaveInventory = async () => {
    try {
      setState((prev) => ({ ...prev, saving: true }));
      const { menus, inventoryDate, inventoryNotes, selectedEmplacement } =
        state;

      const itemsToUpdate = menus.filter((item) => item.difference !== 0);
      if (itemsToUpdate.length === 0) {
        toast.info("Aucune modification à sauvegarder");
        return;
      }

      const inventoryData = {
        _id: selectedInventory ? selectedInventory._id : null,
        organisation_id: organisationId,
        date: inventoryDate,
        notes: inventoryNotes,
        emplacement: selectedEmplacement.name, // Added
        caisse_id: selectedEmplacement.caisse_id, // Added
        created_by: infoUser.id,
        items: menus.map((menu) => ({
          produit_id: menu._id,
          current_stock: menu.currentStock,
          inventory_count: menu.inventoryCount,
          difference: menu.difference,
          notes: menu.notes,
        })),
        adjustments: itemsToUpdate.map((item) => ({
          produit_id: item._id,
          old_quantity: item.currentStock,
          new_quantity: item.inventoryCount,
          difference: item.difference,
          notes: item.notes || "",
        })),
      };

      const success = await onSave(inventoryData);
      if (success) onClose();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setState((prev) => ({ ...prev, saving: false }));
    }
  };

  const {
    loading,
    saving,
    inventoryDate,
    inventoryFilter,
    inventoryNotes,
    loadAllProducts,
  } = state;
  const filteredMenus = getFilteredMenus();
  const summary = getSummary();

  const summaryItems = [
    {
      label: t("Produits"),
      value: summary.totalItems,
      icon: "fa-box",
      color: "text.primary",
    },
    {
      label: t("Avec changements"),
      value: summary.itemsWithChanges,
      icon: "fa-arrows-rotate",
      color: "info.main",
    },
    {
      label: t("Augmentations"),
      value: summary.positiveDifferences,
      icon: "fa-arrow-trend-up",
      color: "success.main",
    },
    {
      label: t("Diminutions"),
      value: summary.negativeDifferences,
      icon: "fa-arrow-trend-down",
      color: "error.main",
    },
    {
      label: t("Différence totale"),
      value: summary.totalDifference,
      icon: "fa-scale-balanced",
      color:
        summary.totalDifference > 0
          ? "success.main"
          : summary.totalDifference < 0
            ? "error.main"
            : "text.secondary",
    },
  ];

  const getStatusChip = (difference) => {
    if (difference > 0) {
      return { color: "success", icon: "fa-arrow-up", label: t("Excédent") };
    } else if (difference < 0) {
      return { color: "error", icon: "fa-arrow-down", label: t("Manquant") };
    } else {
      return { icon: "fa-check", label: t("OK") };
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" fullScreen>
      <DialogTitle sx={{ bgcolor: "primary.main", color: "#fff" }}>
        <Typography variant="h6">
          <i className="fa-solid fa-boxes-stacked" style={{ marginRight: 8 }} />
          {t("Nouvel Inventaire")}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
          disabled={saving}
        >
          <i className="fa-solid fa-xmark" />
        </IconButton>
      </DialogTitle>

      {loading && <LinearProgress />}

      <DialogContent dividers>
        {/* <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            "Saisissez les quantités physiques trouvées en inventaire. Les différences seront calculées automatiquement."
          )}
        </Alert> */}

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <TextField
              label={t("Date de l'inventaire")}
              type="date"
              fullWidth
              value={inventoryDate}
              onChange={(e) =>
                setState((prev) => ({ ...prev, inventoryDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={t("Rechercher un produit")}
              placeholder={t("Nom, référence...")}
              fullWidth
              value={inventoryFilter}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  inventoryFilter: e.target.value,
                }))
              }
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("Emplacement")}</InputLabel>
              <Select
                // Use the name string as the value for the Select component
                value={state.selectedEmplacement.name}
                label={t("Emplacement")}
                onChange={(e) => handleEmplacementChange(e.target.value)}
                disabled={saving}
              >
                {getEmplacements().map((emp) => (
                  <MenuItem key={emp.name} value={emp.name}>
                    {emp.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={loadAllProducts}
                  onChange={(e) =>
                    handleToggleLoadAllProducts(e.target.checked)
                  }
                  disabled={saving || Boolean(selectedInventory)}
                />
              }
              label={t("Charger tous les produits")}
            />
          </Grid>

          {!loadAllProducts && (
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                {t(
                  "Recherchez et ajoutez les produits un par un à l'inventaire.",
                )}
              </Typography>
              <ProductSelector
                organisation={organisationId}
                selectedProduct={null}
                onProductSelect={handleAddProduct}
                extraSearchParams={{ includeStock: true }}
                showClearOption={false}
                closedLabel={t("Rechercher et ajouter un produit...")}
                searchPlaceholder={t("Nom, référence...")}
              />
            </Grid>
          )}
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{
            padding: "5px 0px",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(5, 1fr)" },
          }}
        >
          {summaryItems.map((item, index) => (
            <Grid item key={index}>
              <Paper
                sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className={`fa-solid ${item.icon}`}
                    style={{ fontSize: 20 }}
                  />
                </Box>
                <Box>
                  <Typography variant="h5" color={item.color}>
                    {index === 4 && item.value > 0 ? "+" : ""}
                    {item.value}
                  </Typography>
                  <Typography color="text.secondary">{item.label}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          {t("Produits")} ({filteredMenus.length})
        </Typography>

        {filteredMenus.length === 0 ? (
          // after
          <Alert severity="info">
            {loadAllProducts
              ? t("Aucun produit trouvé. Vérifiez vos filtres.")
              : t(
                  "Aucun produit ajouté. Utilisez la recherche ci-dessus pour ajouter des produits à l'inventaire.",
                )}
          </Alert>
        ) : (
          <Paper sx={{ maxHeight: 400, overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="25%">{t("Produit")}</TableCell>
                  <TableCell align="center" width="15%">
                    {t("Stock Actuel")}
                  </TableCell>
                  <TableCell width="20%">{t("Inventaire Physique")}</TableCell>
                  <TableCell align="center" width="15%">
                    {t("Différence")}
                  </TableCell>
                  <TableCell width="15%">{t("Notes")}</TableCell>
                  <TableCell width="10%">{t("Statut")}</TableCell>
                  {!loadAllProducts && <TableCell align="center" width="5%" />}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredMenus.map((menu, index) => {
                  const status = getStatusChip(menu.difference);
                  return (
                    <TableRow
                      key={menu._id}
                      sx={{
                        bgcolor:
                          menu.difference !== 0
                            ? menu.difference > 0
                              ? "rgba(76, 175, 80, 0.08)"
                              : "rgba(244, 67, 54, 0.08)"
                            : "inherit",
                      }}
                    >
                      <TableCell>
                        <Typography fontWeight="bold">
                          {menu.titre?.fr || menu.titre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {menu.reference || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="body2">
                          {menu.currentStock}
                        </Typography>
                        {/* {menu.stock?.quantite_alerte &&
                          menu.currentStock <= menu.stock.quantite_alerte && (
                            <Chip
                              size="small"
                              label={t("Stock bas")}
                              color="warning"
                              sx={{ mt: 0.5 }}
                            />
                          )} */}
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          type="text"
                          fullWidth
                          placeholder="-"
                          onChange={(e) =>
                            handleUpdate(
                              index,
                              "inventoryCount",
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                            )
                          }
                          disabled={saving}
                          error={menu.inventoryCount < 0}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Typography
                          fontWeight="bold"
                          color={
                            menu.difference > 0
                              ? "success.main"
                              : menu.difference < 0
                                ? "error.main"
                                : "text.secondary"
                          }
                        >
                          {menu.difference > 0 ? "+" : ""}
                          {menu.difference}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={menu.notes}
                          placeholder={t("Notes...")}
                          onChange={(e) =>
                            handleUpdate(index, "notes", e.target.value)
                          }
                          disabled={saving}
                          multiline
                          rows={1}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          color={status.color}
                          label={
                            <>
                              <i
                                className={`fa-solid ${status.icon}`}
                                style={{ marginRight: 6 }}
                              />
                              {status.label}
                            </>
                          }
                        />
                      </TableCell>
                      {!loadAllProducts && (
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveMenu(menu._id)}
                            disabled={saving}
                            title={t("Retirer")}
                          >
                            <i
                              className="fa-solid fa-trash"
                              style={{ fontSize: 14, color: "#dc3545" }}
                            />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button
          onClick={onClose}
          disabled={saving}
          startIcon={<i className="fa-solid fa-xmark" />}
        >
          {t("Annuler")}
        </Button>
        <Box sx={{ flex: 1 }} />
        {/* <Button
          variant="outlined"
          startIcon={<i className="fa-solid fa-print" />}
          onClick={() => window.print()}
          disabled={saving || filteredMenus.length === 0}
        >
          {t("Imprimer")}
        </Button> */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveInventory}
          disabled={saving || summary.itemsWithChanges === 0}
          startIcon={<i className="fa-solid fa-save" />}
        >
          {saving ? t("Enregistrement...") : t("Enregistrer l'inventaire")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryModal;
